/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import React from 'react';

import { MapLayer, MapLayerProps, withLeaflet } from 'react-leaflet';
import L from 'leaflet';
import { RefObject } from 'react';
import {
  DisplayedGenericMapElement,
  GenericMapAgreementLine,
  GeoJsonObject,
  HighlightedMarkers,
  ISmartMapFilterType,
  SmartMapElementType,
} from '../../models/GenericMapModels.model';
import { createSmartMapElementTooltip } from '../../utils/genericMapUtils';
import '../smartMap/smartMap.scss';
import { chartCategories } from '../smartMap/smartMapStatistics/smartMapDashboard';
import { locale } from '../../common/localization/localizationService';
import {
  faEdit,
  faInfo,
  faSearchPlus,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { openRadialContextMenu } from './smartMapContextMenuUtils';

export interface IGeoJsonMapLayerProps extends MapLayerProps {
  routes: GeoJsonObject;
  agreements: DisplayedGenericMapElement[];
  ref?: RefObject<MapLayer>;
  genericMapLayers: GenericMapLayers;
  isAgreementsVisible: boolean;
  bins: DisplayedGenericMapElement[];
  places: DisplayedGenericMapElement[];
  highlightedMarkers: HighlightedMarkers;
  selectedRouteIds: string[];
  filterType: ISmartMapFilterType;
}

export interface GenericMapLayers {
  agreementsLayer: boolean;
  routesLayer: boolean;
  placesLayer: boolean;
  binsLayer: boolean;
}
export class GeoJsonMapLayer extends MapLayer<IGeoJsonMapLayerProps, any> {
  private routeColors: { [key: string]: string };
  private colorPalette: string[];

  private geojsonLayer: L.GeoJSON;

  private getColorForRoute(route: string): string {
    if (!this.routeColors[route]) {
      const usedColors = new Set(Object.values(this.routeColors));
      const availableColors = this.colorPalette.filter(
        (color) => !usedColors.has(color)
      );

      // Assign a new color if available, otherwise, fallback to default assignment
      this.routeColors[route] =
        availableColors.length > 0
          ? availableColors[0]
          : this.colorPalette[
              Object.keys(this.routeColors).length % this.colorPalette.length
            ];
    }
    return this.routeColors[route];
  }
  public createLeafletElement(): L.GeoJSON {
    if (!this.routeColors) {
      this.initializeInstanceFields();
    }

    this.geojsonLayer = L.geoJSON(undefined, {
      pointToLayer: (feature, latlng) => {
        const route = feature.properties?.routeId;
        const fillColor = route ? this.getColorForRoute(route) : '#ff7800';
        return L.circleMarker(latlng, {
          radius: 6,
          fillColor,
          color: '#000',
          weight: 1,
          opacity: 1,
          fillOpacity: 0.8,
        });
      },
    });
    this.loadRoutes(this.props);

    return this.geojsonLayer;
  }

  public updateLeafletElement(
    fromProps: IGeoJsonMapLayerProps,
    toProps: IGeoJsonMapLayerProps
  ): void {
    this.geojsonLayer.clearLayers();
    this.routeColors = {};
    this.loadData(toProps);
  }

  private loadData(props: IGeoJsonMapLayerProps): void {
    this.geojsonLayer.clearLayers();
    this.routeColors = {};
    this.loadRoutes(props);
  }

  private loadRoutes(props: IGeoJsonMapLayerProps): void {
    const { routes, agreements, genericMapLayers, bins, places } = props;
    if (routes && genericMapLayers.routesLayer) {
      this.geojsonLayer.addData(routes as any);
    }

    if (
      agreements &&
      genericMapLayers.agreementsLayer &&
      this.props.isAgreementsVisible
    ) {
      this.addAgreementsToMap(agreements);
    }

    if (bins && genericMapLayers.binsLayer && this.props.isAgreementsVisible) {
      this.addBinsToMap(bins);
    }
    if (
      places &&
      genericMapLayers.placesLayer &&
      this.props.isAgreementsVisible
    ) {
      this.addPlacesToMap(places);
    }
  }

  private addAgreementsToMap(agreements: DisplayedGenericMapElement[]): void {
    agreements.forEach((agreement) => {
      let fillColor = '#ff7800'; // orange
      if (this.props.filterType === ISmartMapFilterType.ROUTE) {
        const routeId = this.getMarkerColoring(agreement.routeIds);
        if (routeId.length === 1) {
          fillColor = this.getColorForRoute(routeId[0]);
        } else if (routeId.length > 1) {
          fillColor = '#800080'; // purple
        }
        if (this.isMarkerOnRoute(agreement.routeIds)) {
          this.createAgreementMarker(agreement, fillColor);
        }
      } else {
        this.createAgreementMarker(agreement, fillColor);
      }
    });
  }

  private createAgreementMarker(
    agreement: DisplayedGenericMapElement,
    fillColor: string
  ) {
    const lat = agreement.lat;
    const lon = agreement.lng;

    const squareSizeMeters = 3;
    const metersToDegreesLat = squareSizeMeters / 111320;
    const metersToDegreesLon =
      squareSizeMeters / (111320 * Math.cos(lat * (Math.PI / 180)));
    const bounds: L.LatLngBoundsLiteral = [
      [
        lat - metersToDegreesLat / 2,
        lon - metersToDegreesLon / 2,
      ] as L.LatLngTuple,
      [
        lat + metersToDegreesLat / 2,
        lon + metersToDegreesLon / 2,
      ] as L.LatLngTuple,
    ];
    const rectangle = L.rectangle(bounds, {
      fillColor,
      color: '#000',
      weight: 1,
      opacity: 1,
      fillOpacity: 0.6,
    });

    const tooltipContent = createSmartMapElementTooltip(
      agreement.elements,
      SmartMapElementType.AGREEMENTS
    );

    rectangle.bindTooltip(tooltipContent, {
      permanent: false,
      direction: 'top',
      offset: [0, -5],
      className: 'custom-tooltip', // Optional for custom styling
    });

    rectangle.addTo(this.geojsonLayer);
  }

  private addBinsToMap(bins: DisplayedGenericMapElement[]): void {
    bins.forEach((bin) => {
      if (bin.lat && bin.lng) {
        let fillColor = '#add8e6'; // light blue
        let opacity = this.props.highlightedMarkers.category ? 0.1 : 0.8;
        if (this.props.filterType === ISmartMapFilterType.ROUTE) {
          const routeId = this.getMarkerColoring(bin.routeIds);
          if (routeId.length === 1) {
            fillColor = this.getColorForRoute(routeId[0]);
          } else if (routeId.length > 1) {
            fillColor = '#808000'; // olive
          }
        }
        if (
          this.props.highlightedMarkers.category === chartCategories.bins &&
          bin.elements.find(
            (element: GenericMapAgreementLine) =>
              element.unitName === this.props.highlightedMarkers.value ||
              (!element.unitName &&
                this.props.highlightedMarkers.value ===
                  locale.smartMap.dashboard._unknown)
          )
        ) {
          opacity = 1;
        }
        if (this.props.filterType === ISmartMapFilterType.ROUTE) {
          if (this.isMarkerOnRoute(bin.routeIds)) {
            this.createBinMarker(bin, fillColor, opacity);
          }
        } else {
          this.createBinMarker(bin, fillColor, opacity);
        }
      }
    });
  }

  private createBinMarker(
    bin: DisplayedGenericMapElement,
    fillColor: string,
    opacity: number
  ): void {
    const lat = bin.lat;
    const lon = bin.lng;

    const scaleFactor = 0.00003;
    // const lonFactor = scaleFactor / Math.cos(lat * (Math.PI / 180));

    const pentagonCoords: L.LatLngTuple[] = [];

    const numSides = 5;
    const angleOffset = -Math.PI / 2; // start at the top (12 o'clock)
    for (let i = 0; i < numSides; i++) {
      const angle = angleOffset + (i * 2 * Math.PI) / numSides;
      const dx = scaleFactor * Math.cos(angle);
      const dy = scaleFactor * Math.sin(angle);
      const pointLat = lat + dy;
      const pointLng = lon + dx / Math.cos((lat * Math.PI) / 180);
      pentagonCoords.push([pointLat, pointLng]);
    }
    // Close the shape by connecting last to first
    pentagonCoords.push(pentagonCoords[0]);

    const polygonOptions: L.PolylineOptions = {
      fillColor: fillColor,
      color: this.props.highlightedMarkers.category ? fillColor : 'black', // Border color (stroke)
      weight: 1, // Border thickness
      opacity: 1,
      fillOpacity: opacity,
    };

    if (
      this.props.highlightedMarkers.category === chartCategories.bins &&
      bin.elements.find(
        (element: GenericMapAgreementLine) =>
          element.unitName === this.props.highlightedMarkers.value ||
          (!element.unitName &&
            this.props.highlightedMarkers.value ===
              locale.smartMap.dashboard._unknown)
      )
    ) {
      polygonOptions.color = 'black'; // Highlight border color
    }

    const binMarker = L.polygon(pentagonCoords, polygonOptions);

    const tooltipContent = createSmartMapElementTooltip(
      bin.elements,
      SmartMapElementType.BINS
    );
    binMarker.bindTooltip(tooltipContent, {
      permanent: false,
      direction: 'top',
      offset: [0, -5],
      className: 'smart-map-marker-tooltip', // Optional for custom styling
    });

    binMarker.on('contextmenu', (e: L.LeafletMouseEvent) => {
      openRadialContextMenu(this.props.leaflet.map, e, [
        {
          icon: <FontAwesomeIcon icon={faEdit} />,
          onClick: () => this.handleEdit(bin),
          label: locale.smartMap.contextMenu._edit,
        },
        {
          icon: <FontAwesomeIcon icon={faInfo} />,
          onClick: () => this.handleShowInfo(bin),
          label: locale.smartMap.contextMenu._info,
        },
        {
          icon: <FontAwesomeIcon icon={faSearchPlus} />,
          onClick: () => this.handleZoomIn(bin),
          label: locale.smartMap.contextMenu._zoomIn,
        },
      ]);
    });

    binMarker.addTo(this.geojsonLayer);
  }

  private addPlacesToMap(places: DisplayedGenericMapElement[]): void {
    places.forEach((place) => {
      if (place.lat && place.lng) {
        let fillColor = '#00ff00'; // green
        if (this.props.filterType === ISmartMapFilterType.ROUTE) {
          const routeId = this.getMarkerColoring(place.routeIds);
          if (routeId.length === 1) {
            fillColor = this.getColorForRoute(routeId[0]);
          } else if (routeId.length > 1) {
            fillColor = '#008000'; // dark green
          }
          if (this.isMarkerOnRoute(place.routeIds)) {
            this.createPlaceMarkerToMap(place, fillColor);
          }
        } else {
          this.createPlaceMarkerToMap(place, fillColor);
        }
      }
    });
  }

  private createPlaceMarkerToMap(
    place: DisplayedGenericMapElement,
    fillColor: string
  ) {
    const lat = place.lat;
    const lon = place.lng;

    const scaleFactor = 0.00002;
    const lonFactor = scaleFactor / Math.cos(lat * (Math.PI / 180));

    const triangleCoords: L.LatLngTuple[] = [
      [lat + scaleFactor, lon], // Top vertex
      [lat - scaleFactor, lon + lonFactor], // Bottom right
      [lat - scaleFactor, lon - lonFactor], // Bottom left
      [lat + scaleFactor, lon], // Connect back to top
    ];

    const trianglePolygon = L.polygon(triangleCoords, {
      fillColor: fillColor,
      color: 'black',
      weight: 1,
      opacity: 1,
      fillOpacity: 0.8,
    });

    trianglePolygon.addTo(this.geojsonLayer);
  }

  private getMarkerColoring(routes: string[]): string[] {
    const colors: string[] = [];
    routes.forEach((route) => {
      if (this.routeColors[route]) {
        colors.push(route);
      }
    });
    return colors;
  }

  private isMarkerOnRoute(routeIds: string[]): boolean {
    return routeIds.some((id) => this.props.selectedRouteIds.includes(id));
  }

  private initializeInstanceFields(): void {
    this.routeColors = {};
    this.colorPalette = [
      '#0078ff', // blue
      '#ff0000', // red
      '#00ffff', // cyan
      '#ffff00', // yellow
      '#ff00ff', // magenta
      '#800000', // maroon
      '#000080', // navy
      '#008080', // teal
      '#c0c0c0', // silver
      '#a52a2a', // brown
      '#f0e68c', // khaki
      '#d2691e', // chocolate
      '#ff1493', // deep pink
      '#00ced1', // dark turquoise
      '#9932cc', // dark orchid
      '#8b0000', // dark red
      '#556b2f', // dark olive green
      '#6a5acd', // slate blue
      '#708090', // slate gray
      '#00fa9a', // medium spring green
      '#4682b4', // steel blue
      '#d2b48c', // tan
      '#d87093', // pale violet red
      '#dda0dd', // plum
      '#add8e6', // light blue
      '#b0c4de', // light steel blue
    ];
  }

  private handleEdit(bin: any) {
    console.log('EDIT:', bin);
  }
  private handleShowInfo(bin: any) {
    console.log('INFO', bin);
  }
  private handleZoomIn(bin: any) {
    console.log('ZOOMIN', bin);
  }
}

export default withLeaflet(GeoJsonMapLayer);
